/**
 * useCompareLayerDetection - Auto-registers the Compare layer when a document has comparison results
 *
 * Task 242: Bridges the gap between "compare workflow completed" and
 * "Compare layer appears in the left panel".
 *
 * The comparison API requires a WORKFLOW PROJECT ID (not a matter ID).
 * This hook uses useMatterWorkflowsQuery for shared/cached workflow fetching,
 * then probes each compare workflow for findings matching the active document.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLayerStore, layerSelectors } from '../stores/layerStore';
import { useMatterWorkflowsQuery } from '../queries/useMatterQueries';
import { comparisonApiService } from '../services/comparisonApi';
import type { MatterWorkflow } from '../types/matter';

// Plan 167: read explicit-selection marker so passive probes can skip the
// short-circuit safely without overwriting a user's choice.

// Module-level cache shared across all hook instances to prevent duplicate probes
const probedDocuments = new Set<string>();
const inFlightProbes = new Map<string, Promise<void>>();
const staleChecked = new Set<string>();

// Plan 38 follow-up: subscriber bumps. Symmetric with useIssueLayerDetection
// (commit a6f4f307, issue #771): clearing the probe cache alone doesn't
// re-run the layer-detection effect, because the effect's deps include
// workflowsData, which TanStack reuses by reference when only the
// underlying findings changed. A dismiss that doesn't add a new workflow
// row leaves the effect dormant and the layer state stale.
//
// Each mounted hook registers a tiny state-bumper; clearCompareProbeCache
// invokes them all to force a re-render + effect re-run.
const probeBumpers = new Set<() => void>();

/** Clear probe cache for a document (e.g., after new analysis completes). */
export function clearCompareProbeCache(documentId?: string) {
  if (documentId) {
    probedDocuments.delete(documentId);
    inFlightProbes.delete(documentId);
    staleChecked.delete(documentId);
  } else {
    probedDocuments.clear();
    inFlightProbes.clear();
    staleChecked.clear();
  }
  probeBumpers.forEach((bump) => bump());
}

/**
 * Whether a matter's run can hold a published comparison worth probing for.
 *
 * #1223: 'failed' is excluded. Plan 193 added it for runs the hard gate
 * blocked; Plan 195 retired that gate, so a blocked run now completes, and a
 * failed run publishes no document_comparisons row.
 */
export function isProbeableCompareRun(w: Pick<MatterWorkflow, 'workflow_type' | 'status'>): boolean {
  return w.workflow_type === 'compare'
    && (w.status === 'completed' || w.status === 'reviewing');
}

/**
 * Detect whether a document has comparison findings and register the Compare layer.
 *
 * @param matterId - The matter ID (NOT the project/workflow ID)
 * @param documentId - The active document ID
 */
export function useCompareLayerDetection(
  matterId: string | null,
  documentId: string | null
): void {
  const addAvailableLayer = useLayerStore((state) => state.addAvailableLayer);
  const hasLayer = useLayerStore((state) =>
    documentId ? layerSelectors.getAvailableLayers(state, documentId).includes('compare') : false
  );
  // Plan 167: when the layer was registered automatically (no explicit marker),
  // the detection effect should still re-probe to pick up newer runs after
  // rehydrate — the previous unconditional `if (hasLayer) return` blocked that.
  const hasExplicitMarker = useLayerStore((state) =>
    documentId ? state.explicitLayerSelections[documentId]?.['compare'] === true : false
  );
  const removeAvailableLayer = useLayerStore((state) => state.removeAvailableLayer);

  const { data: workflowsData } = useMatterWorkflowsQuery(matterId);

  // Plan 38 follow-up: re-render counter bumped by clearCompareProbeCache.
  // Used as a dep on both effects below so they re-run even when
  // workflowsData hasn't changed by reference (the common case for dismiss).
  const [probeBump, setProbeBump] = useState(0);
  useEffect(() => {
    const bump = () => setProbeBump((n) => n + 1);
    probeBumpers.add(bump);
    return () => {
      probeBumpers.delete(bump);
    };
  }, []);

  // Plan 195: detection is driven entirely by quality_tier (single source of
  // truth, derived from alignment_stats). Was previously: (a) liveFindings,
  // (b) comparison_status === 'failed' + failure_reason === 'unrelated_documents'
  // (hard-gate signal, retired in Plan 195), (c) quality_tier === 'medium'.
  // Now: liveFindings OR quality_tier in {medium, blocked}. This keeps
  // zero-finding blocked runs visible (Plan 193's primary feature) without
  // the dead failure_reason coupling.
  const probeUnion = useCallback(async (docId: string): Promise<{
    hasContent: boolean;
    sourceRunId: string | null;
  }> => {
    const probe = await comparisonApiService.getDocumentCompareFindingsUnion(docId, false);
    const liveFindings = (probe.findings ?? []).filter((f) => !f.dismissed_at);
    const lowQuality =
      probe.quality_tier === 'medium' || probe.quality_tier === 'blocked';
    return {
      hasContent: liveFindings.length > 0 || lowQuality,
      sourceRunId: probe.source_analysis_run_id ?? null,
    };
  }, []);

  // Stale layer cleanup effect
  useEffect(() => {
    if (!matterId || !documentId || !hasLayer || !workflowsData) return;
    if (staleChecked.has(documentId)) return;
    staleChecked.add(documentId);

    // Cheap optimisation: if the matter has no compare workflows at all,
    // the layer is stale by definition.
    const hasAnyCompare = (workflowsData.workflows || []).some(
      (w) => w.workflow_type === 'compare'
    );
    if (!hasAnyCompare) {
      console.debug('[useCompareLayerDetection] Removing stale compare layer (no compare workflows in matter):', documentId);
      removeAvailableLayer(documentId, 'compare');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { hasContent, sourceRunId } = await probeUnion(documentId);
        if (cancelled) return;
        if (hasContent && sourceRunId) {
          addAvailableLayer(documentId, 'compare', sourceRunId);
        } else {
          console.debug('[useCompareLayerDetection] Removing stale compare layer:', documentId);
          removeAvailableLayer(documentId, 'compare');
        }
      } catch (err) {
        console.warn('[useCompareLayerDetection] Stale-check probe failed:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [matterId, documentId, hasLayer, workflowsData, addAvailableLayer, removeAvailableLayer, probeBump, probeUnion]);

  // Detection effect
  useEffect(() => {
    if (!matterId || !documentId || !workflowsData) return;
    // Plan 167: only short-circuit on hasLayer if there's an explicit marker.
    // Without the marker, allow probe so source updates to latest after refresh.
    if (hasLayer && hasExplicitMarker) return;
    if (probedDocuments.has(documentId)) return;
    if (inFlightProbes.has(documentId)) return;

    const workflows = workflowsData.workflows || [];

    const compareWorkflows = workflows.filter(isProbeableCompareRun);

    if (compareWorkflows.length === 0) {
      probedDocuments.add(documentId);
      return;
    }

    let cancelled = false;

    const probe = (async () => {
      try {
        const { hasContent, sourceRunId } = await probeUnion(documentId);
        if (cancelled) return;
        if (hasContent && sourceRunId) {
          console.debug('[useCompareLayerDetection] Registered compare layer for review doc:', documentId, 'source:', sourceRunId);
          addAvailableLayer(documentId, 'compare', sourceRunId);
        }
      } catch (err) {
        console.warn('[useCompareLayerDetection] Union probe failed:', err);
      } finally {
        probedDocuments.add(documentId);
      }
    })();

    inFlightProbes.set(documentId, probe);
    probe.finally(() => inFlightProbes.delete(documentId));

    return () => {
      cancelled = true;
    };
  }, [matterId, documentId, hasLayer, hasExplicitMarker, workflowsData, addAvailableLayer, probeBump, probeUnion]);

  // When a layer is removed externally, clear the probe cache so re-detection can occur
  const prevHasLayerRef = useRef(hasLayer);
  useEffect(() => {
    if (prevHasLayerRef.current && !hasLayer && documentId) {
      probedDocuments.delete(documentId);
    }
    prevHasLayerRef.current = hasLayer;
  }, [hasLayer, documentId]);
}
